import { Subscription } from 'rxjs';
import { NzMessageService } from 'ng-zorro-antd/message';

import { Component, HostBinding, EventEmitter, Output, ElementRef, OnInit, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { FleetCardsFeatureFlagService } from '@app/services/feature-flags/fleet-cards';
import { FleetCardsDataService } from '@app/services/fleet-cards-data';
import { DocumentsService } from '@app/admin/fleet-card/card-application/documents.service';
import { Session } from '@app/services/session';
import { PendoAnalytics } from '@app/services/pendo-analytics';
import { StatsigService } from '@services/statsig.service';

import { IFleetCardCompany } from '@app/admin/fleet-card/shared/fleet-cards-waitlist/fleet-cards-waitlist.interface';

import { FLEET_CARD_APPLICATION_STATUS } from '@app/dashboard/fleet-cards/fleet-card-status';
import { HTTP_STATUS_CODE } from '@services/server-api';

@Component({
  selector: 'fleetcard-tos-banner',
  templateUrl: './fleetcard-tos-banner.component.html',
  styleUrls: ['./fleetcard-tos-banner.component.scss'],
  providers: [DocumentsService],
})

export class FleetcardTOSBannerComponent implements OnInit {
  @Output() public onBannerClose = new EventEmitter();
  @Output() public onBannerOpen = new EventEmitter();
  public company: IFleetCardCompany;
  public ccmdStatus: string;
  public bankAccountLinked: boolean;
  public fleetCardLinkBankAccountClicked: boolean;
  public header: string = $localize`We’ve updated the Motive Spend Management Services Agreement`;
  public btnText: string = $localize`Review now`;
  private el: HTMLElement;
  private submitSuccessSubscription: Subscription;
  public tosConfig: any;

  constructor(
    private session: Session,
    private fleetCardsFeatureFlagService: FleetCardsFeatureFlagService,
    private fleetCardsDataService: FleetCardsDataService,
    private pendo: PendoAnalytics,
    private statsigService: StatsigService,
    private message: NzMessageService,
    el: ElementRef,
  ) {
    this.el = el.nativeElement;
    this.tosConfig = this.statsigService.evaluateDynamicConfig('fleetcard-tos-config').value;
  }
  @HostBinding('class.hide') private shouldHideBanner: boolean = true;

  public ngOnInit() {
    if (
      this.session.isAdmin()
      && this.session.getFleetCardApplicationStatus() === FLEET_CARD_APPLICATION_STATUS.APPROVED
      && this.fleetCardsFeatureFlagService.isFleetCardsTosEnabled()
    ) {
      this.fleetCardsDataService.getCompanyCardMetadataStream(false).subscribe((company: IFleetCardCompany) => {
        const cardApplicationSubmittedAt: Date =
          new Date(company.cardApplication?.submittedAt || company.cardApplication?.createdAt);
        const cardApplicationBefore15thDec2024: boolean = cardApplicationSubmittedAt < new Date('2024-12-16');
        const canceledStatus = ['canceled', 'disqualified', 'blocklist'];
        if (!company.termsOfService && cardApplicationBefore15thDec2024 && !canceledStatus.includes(company.status)) {
          this.showBanner();
          this.shouldHideBanner = false;
        }
      });
    }

    this.submitSuccessSubscription = this.fleetCardsDataService.submitSuccess$.subscribe(() => {
      this.closeBanner();
    });
  }

  public showBanner(): void {
    this.pendo.trackEvent('Fleetcard - Terms of Service - Exposure - Banner', {});
    this.onBannerOpen.emit();
  }

  public handleSecondaryCTAClick(): void {
    this.pendo.trackEvent('Fleetcard - Terms of Service - Click - Review', {});
    window.open('https://gomotive.com/legal/terms-of-service/expense-management-cards/#The-Motive-Platform', '_blank');
  }

  public handlePrimaryCTAClick(): void {
    this.pendo.trackEvent('Fleetcard - Terms of Service - Click - Accept', {});
    this.fleetCardsDataService.updateCCMDTermsOfService().subscribe(
      () => { this.handleSuccessResponse(); },
      (error: HttpErrorResponse) => { this.handleErrorResponse(error);
      });
  }

  private handleSuccessResponse(): void {
    this.pendo.trackEvent('Fleetcard - Terms of Service - Click - I accept', {});
    this.message.success($localize`Thank you. We’ve received your acknowledgment.`);
    this.closeBanner();
  }

  private handleErrorResponse(error: HttpErrorResponse): void {
    if (error.status === HTTP_STATUS_CODE.TooManyRequests) {
      this.handleSuccessResponse();
    } else this.message.error($localize`Something went wrong.`);
  }

  private closeBanner(): void {
    this.onBannerClose.emit();
    this.shouldHideBanner = true;
  }
}
